// Fiche d'une étude — le détail du terrain : ce qui a été recueilli, entretien
// par entretien, observation par observation.
//
// La liste des études (`ListScreens.tsx`) ne montre que le cadrage (objectif,
// type, statut). Cliquer une carte mène ici, où chaque verbatim recueilli
// pendant l'étude est listé avec qui l'a dit, dans quel document, à quel
// moment — la même lecture que les preuves d'un insight (`InsightsScreen`),
// pour que l'œil retrouve les mêmes repères d'un écran à l'autre.
import { useProductContext, useStudy, useStudyVerbatims } from '../api/queries';
import type { Evidence, Sentiment } from '../api/types';
import { ApiRequestError } from '../api/client';
import { EmptyState, ErrorBanner, Eyebrow, Note, Spinner } from '../components/ui';
import { useCanWrite } from '../write/useCanWrite';
import { ImportTranscriptForm, WriteAction } from '../write/Forms';
import { Link } from '../router';
import { StudyFramingForm, StudyProtocolSection } from '../studies/StudyProtocolSection';
import { StepHeader, useStudyPath } from '../studies/StudyPath';
import { QuestionnaireParticipants, QuestionnaireResults } from '../studies/QuestionnaireSections';
import { StudySources } from '../sources/StudySources';
import { KIND_BY_KEY, studyKind } from '../studies/kinds';
import { StudyInterviewsSection } from './InterviewsScreen';
import { StudyInsightsSection } from '../insights/StudyInsightsSection';
import { StudyInvitationsSection } from '../invitations/StudyInvitationsSection';
import { Mesure0Block } from '../mesure0/Mesure0Block';
import { Mesure0Score } from '../mesure0/Mesure0Score';
import { StatusChip, StudyTypeChip, TagChip } from '../taxonomy/Chips';
import { TabBar, TabPanel, useTabs } from '../components/Tabs';
import { FieldItemsSection } from '../capture/FieldItems';
import { rememberStudy } from '../capture/CaptureButton';
import { useEffect } from 'react';
import './project.css';

const SENTIMENT_LABEL: Record<Sentiment, string> = {
  pos: 'positif',
  neu: 'neutre',
  neg: 'négatif',
};

function errorMessage(cause: unknown): string {
  if (cause instanceof ApiRequestError) return cause.message;
  if (cause instanceof Error) return cause.message;
  return 'Une erreur inattendue est survenue.';
}

function formatTimecode(ms: number | null | undefined): string | null {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return null;
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}

function VerbatimList({ projectId, studyId }: { projectId: string; studyId: string }) {
  const query = useStudyVerbatims(studyId);
  const canWrite = useCanWrite(projectId);

  if (query.isPending) {
    return (
      <div className="screen-loading">
        <Spinner label="Chargement des verbatims" />
      </div>
    );
  }
  if (query.isError) {
    return <ErrorBanner>{errorMessage(query.error)}</ErrorBanner>;
  }
  if (query.data.length === 0) {
    return (
      <EmptyState
        title="Rien de recueilli pour l’instant"
        description="Versez une transcription d’entretien ou d’observation : les verbatims significatifs en sont extraits, avec leur locuteur et leur horodatage, et rattachés à cette étude."
        action={
          canWrite ? (
            <WriteAction
              label="Importer une transcription"
              title="Importer une transcription"
              description="Le texte est analysé côté serveur. Les verbatims extraits seront rattachés à CETTE étude."
            >
              {(close) => (
                <ImportTranscriptForm projectId={projectId} studyId={studyId} onDone={close} />
              )}
            </WriteAction>
          ) : undefined
        }
      />
    );
  }

  return (
    <ul className="evidence-list">
      {query.data.map((v: Evidence) => {
        const meta = [v.persona_name ?? v.speaker, v.source_ref, formatTimecode(v.ts_ms)].filter(Boolean);
        return (
          <li key={v.id} className="evidence">
            <span className={`dot dot-${v.sentiment ?? 'neu'}`} aria-hidden="true" />
            <span className="evidence-body">
              <span className="evidence-text">« {v.text} »</span>
              {meta.length > 0 ? <span className="evidence-meta">{meta.join(' · ')}</span> : null}
              {v.tags && v.tags.length > 0 ? (
                <span className="chips">
                  {v.tags.map((t) => (
                    <TagChip key={t} tag={t} />
                  ))}
                </span>
              ) : null}
            </span>
            {v.sentiment ? <span className="sr-only">Sentiment {SENTIMENT_LABEL[v.sentiment]}</span> : null}
          </li>
        );
      })}
    </ul>
  );
}

// Une seule ligne d'onglets : préparer, inviter, puis les quatre temps du
// terrain — capter, découper, ranger dans l'atelier, conclure.
type StudyTab = 'etat' | 'protocole' | 'participants' | 'capter' | 'decouper' | 'atelier' | 'conclure' | 'resultats';

const STUDY_TAB_LABELS: Record<StudyTab, string> = {
  etat: 'État des lieux',
  protocole: 'Protocole',
  participants: 'Participants',
  capter: 'Capter',
  decouper: 'Découper',
  atelier: 'Atelier',
  conclure: 'Conclure',
  resultats: 'Résultats',
};

export function StudyDetailScreen({ projectId, studyId }: { projectId: string; studyId: string }) {
  const query = useStudy(studyId);
  const context = useProductContext(projectId);
  const canWrite = useCanWrite(projectId);
  const backTo = `/projet/${encodeURIComponent(projectId)}/etudes`;
  // « Capturer » (barre du bas) proposera d'abord la dernière étude ouverte.
  useEffect(() => rememberStudy(projectId, studyId), [projectId, studyId]);
  // Les onglets dépendent de l'étude : la mesure zéro a son état des lieux, un
  // audit ou un questionnaire n'a pas de séance à laquelle inviter.
  const loadedKind = query.data ? studyKind(query.data.type) : null;
  const studyTabs: StudyTab[] = query.data?.is_baseline
    ? ['etat', 'protocole', 'capter', 'conclure']
    : loadedKind === 'questionnaire'
      // Un questionnaire : ses questions, à qui l'envoyer, ce qu'on a répondu.
      ? ['protocole', 'participants', 'resultats']
      : loadedKind === 'audit'
      ? ['protocole', 'capter', 'decouper', 'atelier', 'conclure']
      : ['protocole', 'participants', 'capter', 'decouper', 'atelier', 'conclure'];
  const [tab, setTab] = useTabs<StudyTab>('etude', studyTabs, studyTabs[0] ?? 'protocole');
  // Où en est chaque temps du terrain : affiché en tête de son onglet.
  const path = useStudyPath({ projectId, studyId, canWrite, canInvite: studyTabs.includes('participants') && loadedKind !== 'questionnaire' });
  const steps = query.data?.is_baseline ? null : path.byKey;
  const tabLabel = (id: StudyTab) => {
    const step = steps && (id === 'capter' || id === 'decouper' || id === 'atelier' || id === 'conclure') ? steps[id] : null;
    return step ? { id, label: `${step.state === 'done' ? '✓ ' : ''}${STUDY_TAB_LABELS[id]}`, count: step.count || null }
      : { id, label: STUDY_TAB_LABELS[id] };
  };

  if (query.isPending) {
    return (
      <main className="screen">
        <div className="screen-loading">
          <Spinner label="Chargement de l’étude" />
        </div>
      </main>
    );
  }

  if (query.isError) {
    return (
      <main className="screen">
        <Link to={backTo} className="zy-retour">
          ← Études
        </Link>
        <ErrorBanner>{errorMessage(query.error)}</ErrorBanner>
        <EmptyState
          title="Étude introuvable dans votre périmètre"
          description="Elle n’existe pas, ou elle appartient à un projet auquel vous n’avez pas accès."
        />
      </main>
    );
  }

  const study = query.data;
  const kind = studyKind(study.type);
  const facts = [
    study.method ? { label: 'Méthode', value: study.method } : null,
    study.target ? { label: 'Cible', value: study.target } : null,
    study.participants != null
      ? { label: (kind && KIND_BY_KEY[kind].participantsLabel) || 'Participants', value: String(study.participants) }
      : null,
    study.period ? { label: 'Période', value: study.period } : null,
  ].filter((f): f is { label: string; value: string } => f !== null);

  return (
    <main className="screen">
      <Link to={backTo} className="zy-retour">
        ← Études
      </Link>

      <div className="zy-page-head__texte">
        <Eyebrow>{study.code ?? study.id}</Eyebrow>
        <h1 className="t-h1">{study.objective ?? 'Sans objectif renseigné'}</h1>
        {/* Les étiquettes à gauche, l'action à droite : elles ne se collent plus. */}
        <div className="study-head-row">
          <div className="test-head-chips">
            <StudyTypeChip type={study.type} />
            <StatusChip value={study.status} />
          </div>
          {canWrite ? (
            <WriteAction label="Modifier le cadrage" title="Cadrage de l’étude" description="Objectif, méthode, cible, participants, période.">
              {(close) => <StudyFramingForm projectId={projectId} study={study} onDone={close} />}
            </WriteAction>
          ) : null}
        </div>
        {study.origin_test_id ? (
          <p className="zy-intro">
            Issue du test{' '}
            <Link
              to={`/projet/${encodeURIComponent(projectId)}/tests/${encodeURIComponent(study.origin_test_id)}`}
              className="card-title-link"
            >
              {study.origin_test_code ?? study.origin_test_objective ?? study.origin_test_id}
            </Link>
          </p>
        ) : null}
      </div>

      {facts.length > 0 ? (
        <dl className="facts">
          {facts.map((f) => (
            <div key={f.label}>
              <dt>{f.label}</dt>
              <dd>{f.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {study.context ? <p className="card-note">{study.context}</p> : null}

      {/* Un onglet à la fois : préparer, inviter, capter, découper, ranger, conclure. */}
      <TabBar
        label="Sections de l’étude"
        value={tab}
        onChange={setTab}
        tabs={studyTabs.map(tabLabel)}
      />

      {/* L'étude « mesure zéro » porte l'état des lieux déclaré par le
          commanditaire, et le score qu'il en donne : ce que les autres études
          viendront confronter. */}
      {tab === 'etat' ? (
        <TabPanel id="etat">
          <Mesure0Block projectId={projectId} projectName={context.data?.projectName ?? ''} />
          <Mesure0Score projectId={projectId} />
        </TabPanel>
      ) : null}

      {/* Le protocole AVANT le terrain : on prépare, puis on recueille. */}
      {tab === 'protocole' ? (
        <TabPanel id="protocole">
          {study.instrument ? (
            <Note tone="lecture">Les questions du baromètre sont standard : elles ne se modifient pas, pour que les vagues restent comparables.</Note>
          ) : null}
          <StudyProtocolSection projectId={projectId} study={study} canWrite={canWrite && !study.instrument} />
        </TabPanel>
      ) : null}

      {/* On invite d'abord : un audit et un questionnaire n'ont pas de séance. */}
      {tab === 'participants' ? (
        <TabPanel id="participants">
        <div id="etude-participants" />
        {kind === 'questionnaire' ? (
          <QuestionnaireParticipants projectId={projectId} studyId={studyId} canWrite={canWrite} wave={Boolean(study.instrument) && !study.closed_at} />
        ) : (
          <StudyInvitationsSection
            projectId={projectId}
            studyId={studyId}
            canWrite={canWrite}
            defaultSubject={kind === 'test' ? 'Test d’une application' : 'Échange sur votre usage'}
          />
        )}
        </TabPanel>
      ) : null}

      {/* CAPTER : les séances et leur enregistrement, ce qui est pris sur place,
          les transcriptions importées. */}
      {tab === 'capter' ? (
      <TabPanel id="capter">
      {steps ? <StepHeader step={steps.capter} /> : null}
      <div id="etude-captation">
        <StudyInterviewsSection projectId={projectId} studyId={studyId} />
      </div>

      {/* Photos, vidéos, sons et notes pris sur place (058). */}
      <FieldItemsSection projectId={projectId} studyId={studyId} />

      <section className="test-block">
        <div className="block-head">
          <h2 className="t-h3">Transcriptions et notes</h2>
          {canWrite ? (
            <WriteAction
              label="Importer une transcription"
              title="Importer une transcription"
              description="Le texte est analysé côté serveur. Les verbatims extraits seront rattachés à CETTE étude."
            >
              {(close) => (
                <ImportTranscriptForm projectId={projectId} studyId={studyId} onDone={close} />
              )}
            </WriteAction>
          ) : null}
        </div>
        {/* Les transcriptions, à découper ensuite (migration 047). */}
        <StudySources projectId={projectId} studyId={studyId} />
      </section>
      </TabPanel>
      ) : null}

      {/* DÉCOUPER : les verbatims retenus, preuves des insights à venir. */}
      {tab === 'decouper' ? (
        <TabPanel id="decouper">
          {steps ? <StepHeader step={steps.decouper} /> : null}
          <section className="test-block" id="etude-terrain">
            <div className="block-head">
              <h2 className="t-h3">Verbatims retenus</h2>
            </div>
            <VerbatimList projectId={projectId} studyId={studyId} />
          </section>
        </TabPanel>
      ) : null}

      {/* ATELIER : les post-it de l'étude, rangés à la main sur le plan. */}
      {tab === 'atelier' && steps ? (
        <TabPanel id="atelier">
          <StepHeader step={steps.atelier} error={path.error} />
        </TabPanel>
      ) : null}

      {/* RÉSULTATS d'un questionnaire : l'agrégat des réponses. */}
      {tab === 'resultats' ? (
        <TabPanel id="resultats">
          <QuestionnaireResults studyId={studyId} canWrite={canWrite} />
        </TabPanel>
      ) : null}

      {/* CONCLURE : ce qu'on tire du terrain — les insights de l'étude. */}
      {tab === 'conclure' ? (
        <TabPanel id="conclure">
          {steps ? <StepHeader step={steps.conclure} /> : null}
          <div id="etude-insights">
            <StudyInsightsSection projectId={projectId} studyId={studyId} canWrite={canWrite} />
          </div>
        </TabPanel>
      ) : null}
    </main>
  );
}
